import { z } from 'zod';
import { wallClock, zonedToInstant } from './business-time.js';
import { idSchema, instantSchema } from './common.js';
import { actorSchema } from './events.js';
import { fieldTypeSchema } from './fields.js';
import { filterSchema } from './filters.js';
import { AUTOMATED_TASK_TYPES, manualTaskTypeSchema, TASK_DESCRIPTION_MAX, TASK_TYPE_LABELS, } from './notes-tasks.js';
// Pipeline triggers: one rule + one action per trigger; each firing is a `trigger_runs` row.
/** The records a trigger can watch: every pipeline type's, each place entered at a known time. */
export const TRIGGER_SUBJECTS = [
    'project',
    'deal',
    'job',
    'candidate',
    'shortlist',
];
export const triggerSubjectSchema = z.enum(TRIGGER_SUBJECTS);
/**
 * Whether a pipeline's stages can have automations. A shortlisted candidate has one stage, in the
 * default Shortlist Flow pipeline, so an extra Shortlist Flow pipeline holds nobody to run them for.
 */
export const allowsTriggers = (type, isDefault) => type !== 'shortlist' || isDefault;
export const TRIGGER_KINDS = ['stage_entered', 'stage_left', 'relative_to_date'];
export const triggerKindSchema = z.enum(TRIGGER_KINDS);
export const TRIGGER_ACTION_TYPES = ['create_task', 'create_note', 'http_request'];
export const triggerActionTypeSchema = z.enum(TRIGGER_ACTION_TYPES);
/**
 * pending: due at `nextAttemptAt` (planned, claimed, queued, or waiting out a backoff); done:
 * applied, see its outcome; dead: given up.
 */
export const TRIGGER_RUN_STATUSES = ['pending', 'done', 'dead'];
export const triggerRunStatusSchema = z.enum(TRIGGER_RUN_STATUSES);
/** What a done run did: called a URL, created a task or note, or nothing (the re-check failed). */
export const TRIGGER_RUN_OUTCOMES = ['sent', 'created', 'skipped'];
export const triggerRunOutcomeSchema = z.enum(TRIGGER_RUN_OUTCOMES);
/**
 * The dates a `relative_to_date` rule may be anchored on: only ones that usually lie ahead, since
 * a due time already past plans nothing. Only a shortlisted candidate has them; the other
 * subjects' pipelines offer no date rule.
 */
export const TRIGGER_DATE_FIELDS = {
    project: [],
    deal: [],
    job: [],
    candidate: [],
    shortlist: [
        'candidate.interviewDate',
        'candidate.onboardingDate',
        'job.interviewDateTime',
        'job.onboardingDateTime',
        'job.vaPresentationDateTime',
    ],
};
/** A year either way: anything further is a mistake in units, not a plan. */
export const TRIGGER_MAX_DELAY_MINUTES = 365 * 24 * 60;
// ── Schedule ───────────────────────────────────────────────────────────────────────────────
/**
 * When an automated task (`http_request`) may run: ISO weekdays (Monday = 1) and an optional
 * window in minutes of the day, both on the New York clock. A run that falls outside waits for
 * the window to open on the nearest allowed day: the next one, or for a reminder before a date
 * the previous one. Task and note actions always run at once, so they keep every day, no window.
 */
export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
export const WORKING_WEEKDAYS = [1, 2, 3, 4, 5];
/** Where a moved run lands when the automation has no window: 09:00 New York. */
export const TRIGGER_DEFAULT_OPEN_MINUTE = 9 * 60;
const DAY_MS = 86_400_000;
/** `at` on the New York clock: that day's midnight (as if UTC), its ISO weekday, its minute. */
function nyDay(at) {
    const { weekday, asUtc } = wallClock(at);
    const day = asUtc - (asUtc % DAY_MS);
    return { day, isoWeekday: weekday + 1, minute: Math.floor((asUtc - day) / 60_000) };
}
export function scheduleAllows(s, at) {
    const { isoWeekday, minute } = nyDay(at);
    if (!s.allowedWeekdays.includes(isoWeekday))
        return false;
    return (s.allowedFromMinute === null ||
        s.allowedUntilMinute === null ||
        (minute >= s.allowedFromMinute && minute < s.allowedUntilMinute));
}
/**
 * `at` when the schedule allows it; otherwise the opening minute (the window's start, else 09:00)
 * of the nearest allowed time `forward` (later that day or the next allowed day) or `back` (earlier
 * that day or the previous allowed day: a reminder before a date must stay before it).
 */
export function nextAllowedAt(s, at, direction = 'forward') {
    if (scheduleAllows(s, at))
        return at;
    const open = s.allowedFromMinute ?? TRIGGER_DEFAULT_OPEN_MINUTE;
    const { day, isoWeekday, minute } = nyDay(at);
    const allowed = (offset) => s.allowedWeekdays.includes(((((isoWeekday - 1 + offset) % 7) + 7) % 7) + 1);
    const opening = (offset) => new Date(zonedToInstant(day + offset * DAY_MS + open * 60_000));
    const step = direction === 'forward' ? 1 : -1;
    // Same day: before the window opens (forward), or after it closed (back).
    if (allowed(0) &&
        s.allowedFromMinute !== null &&
        (direction === 'forward' ? minute < open : minute >= (s.allowedUntilMinute ?? 1440)))
        return opening(0);
    for (let d = 1; d <= 7; d++)
        if (allowed(d * step))
            return opening(d * step);
    // Unreachable while at least one weekday is allowed (the schema's rule).
    return at;
}
/** When a run anchored at `anchor` is due: moved into the trigger's days and hours, a reminder before a date backwards. */
export function triggerDueAt(t, anchor) {
    return nextAllowedAt(t, new Date(anchor + t.delayMinutes * 60_000), t.delayMinutes < 0 ? 'back' : 'forward');
}
// ── Actions ────────────────────────────────────────────────────────────────────────────────
/**
 * Who a created task goes to. The `job_*` rules read the job (the shortlisted candidate's, or the
 * job itself), so they only apply to `shortlist` and `job` triggers. `record_owner` is the
 * project's or the candidate's owner; a deal or a job has none, so it is its project's owner.
 * A rule that resolves to nobody (no owner, a deactivated member) leaves the task unassigned
 * rather than failing the run.
 */
export const TASK_ASSIGNEE_RULES = [
    'record_owner',
    'job_recruiter',
    'job_coordinator',
    'job_account_manager',
];
export const taskAssigneeSchema = z.discriminatedUnion('rule', [
    z.object({ rule: z.enum(TASK_ASSIGNEE_RULES) }),
    z.object({ rule: z.literal('user'), userId: idSchema }),
]);
/** Templates take `{{entity.field}}` merge fields, rendered by `renderTemplate` over `TEMPLATE_FIELDS` (`templates.ts`). */
export const createTaskConfigSchema = z.object({
    titleTemplate: z.string().trim().min(1).max(300),
    descriptionTemplate: z.string().trim().max(TASK_DESCRIPTION_MAX).nullable().default(null),
    assignee: taskAssigneeSchema,
    /** Due this long after the task is created; null = no due date. */
    dueOffsetMinutes: z.number().int().min(0).max(TRIGGER_MAX_DELAY_MINUTES).nullable().default(null),
    /** A type a person could have picked (the task is an ordinary one), or none. */
    taskType: manualTaskTypeSchema.nullable().default(null),
});
export const createNoteConfigSchema = z.object({
    bodyTemplate: z.string().trim().min(1).max(10_000),
});
/**
 * The admin picks only where to send and the automated task's type: the body is the fixed
 * `TriggerPayload` below and the headers are the CRM's own (content type, idempotency key).
 */
export const httpRequestConfigSchema = z.object({
    method: z.enum(['POST', 'PUT']),
    /** http(s) here; the API allows plain http and private hosts only in development/test, and the worker re-checks at send time. */
    url: z.url({ protocol: /^https?$/ }).max(2000),
    /** The type of the automated task the run shows in My Tasks, due when it sends. */
    taskType: z.enum(AUTOMATED_TASK_TYPES),
});
export const triggerActionSchema = z.discriminatedUnion('actionType', [
    z.object({ actionType: z.literal('create_task'), actionConfig: createTaskConfigSchema }),
    z.object({ actionType: z.literal('create_note'), actionConfig: createNoteConfigSchema }),
    z.object({ actionType: z.literal('http_request'), actionConfig: httpRequestConfigSchema }),
]);
// ── Triggers ───────────────────────────────────────────────────────────────────────────────
const ruleFields = {
    name: z.string().trim().min(1).max(120),
    pipelineId: idSchema,
    stageId: idSchema,
    subject: triggerSubjectSchema,
    kind: triggerKindSchema,
    delayMinutes: z.number().int().min(-TRIGGER_MAX_DELAY_MINUTES).max(TRIGGER_MAX_DELAY_MINUTES),
    dateField: z.string().min(1).max(64).nullable(),
    /** The filter DSL over the subject's fields; validated against its catalogue by the API. */
    condition: filterSchema.nullable(),
    enabled: z.boolean(),
    /** ISO weekdays an automated task may run on (`TriggerSchedule`). */
    allowedWeekdays: z
        .array(z.number().int().min(1).max(7))
        .min(1, 'at least one day')
        .refine((days) => new Set(days).size === days.length, 'each day once'),
    /** The window's start and end in minutes of the New York day; both null = all day. */
    allowedFromMinute: z.number().int().min(0).max(1439).nullable(),
    allowedUntilMinute: z.number().int().min(1).max(1440).nullable(),
};
/** The rules between fields a single field's schema cannot state. */
function checkRule(t, ctx) {
    const issue = (path, message) => ctx.addIssue({ code: 'custom', path: [path], message });
    if (t.kind === 'relative_to_date') {
        const dates = TRIGGER_DATE_FIELDS[t.subject];
        if (!dates.length)
            issue('kind', `a ${t.subject} has no dates to count from`);
        else if (!t.dateField)
            issue('dateField', 'a date to count from');
        else if (!dates.includes(t.dateField))
            issue('dateField', `not a date of a ${t.subject}`);
    }
    else {
        if (t.dateField !== null)
            issue('dateField', 'only for relative_to_date');
        if (t.delayMinutes < 0)
            issue('delayMinutes', 'only relative_to_date may run before');
    }
    if (t.actionType === 'create_task' &&
        t.subject !== 'shortlist' &&
        t.subject !== 'job' &&
        t.actionConfig.assignee.rule.startsWith('job_'))
        issue('actionConfig', 'a job role only exists for a job or a shortlisted candidate');
    if ((t.allowedFromMinute === null) !== (t.allowedUntilMinute === null))
        issue('allowedUntilMinute', 'a window needs a start and an end');
    else if (t.allowedFromMinute !== null &&
        t.allowedUntilMinute !== null &&
        t.allowedFromMinute >= t.allowedUntilMinute)
        issue('allowedUntilMinute', 'the window must end after it starts, the same day');
    if (t.actionType !== 'http_request' &&
        (t.allowedWeekdays.length < ISO_WEEKDAYS.length || t.allowedFromMinute !== null))
        issue('allowedWeekdays', 'only an automated task waits for its days and hours');
}
/** `POST` and full replacement (`PUT`) of a trigger. */
export const triggerInputSchema = z
    .object({
    ...ruleFields,
    delayMinutes: ruleFields.delayMinutes.default(0),
    dateField: ruleFields.dateField.default(null),
    condition: ruleFields.condition.default(null),
    enabled: ruleFields.enabled.default(true),
    allowedWeekdays: ruleFields.allowedWeekdays.default([...ISO_WEEKDAYS]),
    allowedFromMinute: ruleFields.allowedFromMinute.default(null),
    allowedUntilMinute: ruleFields.allowedUntilMinute.default(null),
})
    .and(triggerActionSchema)
    .superRefine(checkRule)
    .meta({
    examples: [
        {
            name: 'Discovery call follow-up',
            pipelineId: '5b3c1c1e-9a43-4a8e-9a43-0f6a2a1b7c01',
            stageId: '5b3c1c1e-9a43-4a8e-9a43-0f6a2a1b7c02',
            subject: 'project',
            kind: 'stage_entered',
            delayMinutes: 0,
            dateField: null,
            condition: null,
            enabled: true,
            allowedWeekdays: [1, 2, 3, 4, 5, 6, 7],
            allowedFromMinute: null,
            allowedUntilMinute: null,
            actionType: 'create_task',
            actionConfig: {
                titleTemplate: 'Book the discovery call with {{project.name}}',
                descriptionTemplate: null,
                assignee: { rule: 'record_owner' },
                dueOffsetMinutes: 1440,
                taskType: null,
            },
        },
    ],
});
/** Quick edits from a list. Anything that changes the rule or the action is a full replacement. */
export const triggerPatchSchema = z
    .object({ name: ruleFields.name, enabled: ruleFields.enabled })
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'nothing to change' });
const triggerFields = {
    id: idSchema,
    ...ruleFields,
    createdBy: idSchema.nullable(),
    createdAt: instantSchema,
    updatedAt: instantSchema,
};
const [taskAction, noteAction, httpAction] = triggerActionSchema.options;
// One closed object per action, not an intersection: OpenAPI renders `.and` as an allOf of
// closed objects that no response can satisfy.
const triggerVariants = (extra) => z.discriminatedUnion('actionType', [
    z.object({ ...triggerFields, ...extra, ...taskAction.shape }),
    z.object({ ...triggerFields, ...extra, ...noteAction.shape }),
    z.object({ ...triggerFields, ...extra, ...httpAction.shape }),
]);
export const triggerSchema = triggerVariants({});
/**
 * An `http_request` trigger's requests are HMAC-signed as webhook deliveries are. Its secret is in
 * this answer only: on create, on a replacement that turns a trigger into an `http_request`, and
 * on `:rotate-secret`. It is never served otherwise.
 */
export const triggerWithSecretSchema = triggerVariants({ signingSecret: z.string().optional() });
export const triggerListQuerySchema = z.object({
    pipelineId: idSchema.optional(),
    stageId: idSchema.optional(),
});
/** Running a trigger for the records already in its stage; `dryRun` only counts. */
export const triggerRunExistingInputSchema = z.object({ dryRun: z.boolean().default(false) });
export const triggerRunExistingSchema = z.object({
    /** Records in the stage now. */
    inStage: z.number().int(),
    /** Runs planned by this call (or that would be, on a dry run). */
    planned: z.number().int(),
    /** Runs that were planned already, left as they are. */
    existing: z.number().int(),
});
// ── Runs ───────────────────────────────────────────────────────────────────────────────────
export const triggerRunRequestSchema = z.object({
    method: z.string(),
    url: z.string(),
    headers: z.record(z.string(), z.string()),
    body: z.string(),
});
/** The response is kept to this many characters: enough to read an error, never a whole page. */
export const TRIGGER_RESPONSE_EXCERPT_MAX = 2000;
/** The sent body is kept to this many characters in the run log: enough to debug, not a copy of the project. */
export const TRIGGER_REQUEST_BODY_MAX = 64_000;
export const triggerRunSchema = z.object({
    id: idSchema,
    triggerId: idSchema,
    subjectType: triggerSubjectSchema,
    subjectId: idSchema,
    status: triggerRunStatusSchema,
    attempts: z.number().int(),
    nextAttemptAt: instantSchema,
    outcome: triggerRunOutcomeSchema.nullable(),
    /** The plan it was checked against, the created task or note id, or why it was skipped. */
    result: z.record(z.string(), z.unknown()).nullable(),
    lastError: z.string().nullable(),
    request: triggerRunRequestSchema.nullable(),
    responseStatus: z.number().int().nullable(),
    responseExcerpt: z.string().nullable(),
    durationMs: z.number().int().nullable(),
    traceparent: z.string().nullable(),
    createdAt: instantSchema,
    processedAt: instantSchema.nullable(),
});
/**
 * What a run was planned against, kept in `result.plan` by the drain that inserts it: the
 * subject's `stage_entered_at` (for `stage_left`, when it left) and, for `relative_to_date`, the
 * date it counts from. The loop re-checks both at fire time and skips the run when they moved.
 * `event` is the outbox event that planned it, with who made it and the stage move it carried;
 * the HTTP payload reports it as `cause`. A malformed `event` is dropped, never the whole plan.
 */
export const triggerRunPlanSchema = z.object({
    stageEnteredAt: instantSchema,
    date: instantSchema.optional(),
    event: z
        .object({
        id: idSchema,
        name: z.string(),
        occurredAt: instantSchema,
        actor: actorSchema.nullable(),
        fromStageId: idSchema.nullable(),
        toStageId: idSchema.nullable(),
    })
        .optional()
        .catch(undefined),
});
/** Breaker names for `http_request` targets: one per organisation and host, as webhooks' are. */
export const TRIGGER_HTTP_BREAKER_PREFIX = 'trigger-http:';
/** Why a run is skipped: worded once for the worker that skips it and the task badge that shows it. */
export const TRIGGER_SKIP_REASONS = {
    triggerDeleted: 'the trigger was deleted',
    triggerOff: 'the trigger is turned off',
    actionChanged: 'the action changed',
    automationChanged: 'the automation changed',
    pipelineArchived: 'the pipeline is archived',
    taskClosed: 'the automated task was closed',
};
export const triggerHttpBreakerName = (organisationId, host) => `${TRIGGER_HTTP_BREAKER_PREFIX}${organisationId}:${host}`;
// ── Merge fields ───────────────────────────────────────────────────────────────────────────
/** The template fields of an action, by the path a validation issue names. */
export function actionTemplates(action) {
    switch (action.actionType) {
        case 'create_task':
            return {
                titleTemplate: action.actionConfig.titleTemplate,
                descriptionTemplate: action.actionConfig.descriptionTemplate ?? '',
            };
        case 'create_note':
            return { bodyTemplate: action.actionConfig.bodyTemplate };
        case 'http_request':
            return {};
    }
}
// ── Payload ────────────────────────────────────────────────────────────────────────────────
/**
 * The body of every `http_request` run: the subject's project and its records, so a receiver
 * never needs a second call. A deal or job is found in `project.deals` / `project.jobs` by
 * `subject.id`; a Candidate Flow candidate belongs to no project, so its run has `project` null
 * and the person in `candidate`. Each record carries a fixed allowlist of its columns (the worker's)
 * and, where the entity has custom fields, every one no role is denied under `custom`. Each
 * record is in one place, and a person is always an object where it is named.
 */
const looseRecord = (shape) => z.looseObject({ id: idSchema, ...shape });
/**
 * Someone a record names: a `member` of the organisation, an `integration` (an API key, named
 * after it, no email) or a `former_member`, whose `name` and `email` are no longer sent.
 */
const payloadPersonSchema = z
    .object({
    id: idSchema,
    kind: z.enum(['member', 'integration', 'former_member']),
    name: z.string().nullable(),
    email: z.string().nullable(),
})
    .nullable();
/** One custom field of a record, set or not: `displayValue` is a select's option label. */
const payloadCustomFieldSchema = z.object({
    id: idSchema,
    key: z.string(),
    label: z.string(),
    type: fieldTypeSchema,
    value: z.unknown(),
    displayValue: z.unknown(),
});
const custom = z.array(payloadCustomFieldSchema);
const payloadStageSchema = z
    .object({
    id: idSchema,
    name: z.string(),
    slug: z.string(),
    terminalKind: z.enum(['won', 'lost']).nullable(),
})
    .nullable();
const payloadPlacementSchema = z.object({
    pipeline: z.object({ id: idSchema, name: z.string() }),
    stage: payloadStageSchema,
    stageEnteredAt: instantSchema,
    archivedAt: instantSchema.nullable(),
});
export const triggerPayloadSchema = z.object({
    /** The run id, also sent as `Idempotency-Key`: a retry is the same delivery. */
    id: idSchema,
    /** When this attempt was made; when the subject entered the stage is `placement.stageEnteredAt`. */
    occurredAt: instantSchema,
    organisationId: idSchema,
    /** The automation and what it asks for: `taskType` is the action the receiver carries out. */
    trigger: z.object({
        id: idSchema,
        name: z.string(),
        kind: triggerKindSchema,
        delayMinutes: z.number().int(),
        dateField: z.string().nullable(),
        taskType: z.enum(AUTOMATED_TASK_TYPES),
        taskTypeLabel: z.string(),
    }),
    /** What happened to make the run due. */
    cause: z.object({
        /** The CRM event that planned the run (`crm.project.stage_changed`, `crm.job.updated`, …); null when the run does not name one. */
        event: z
            .object({
            id: idSchema,
            name: z.string(),
            occurredAt: instantSchema,
            /** Who made the change: a member, the system or an integration. */
            actor: z
                .discriminatedUnion('kind', [
                z.object({ kind: z.literal('user'), user: payloadPersonSchema }),
                z.object({ kind: z.literal('system'), job: z.string().min(1) }),
                z.object({ kind: z.literal('integration'), name: z.string().min(1) }),
            ])
                .nullable(),
        })
            .nullable(),
        /** The stage move that event made; null for a date change, or a side it does not name. */
        fromStage: payloadStageSchema,
        toStage: payloadStageSchema,
        /** What the delay counts from: entering the stage, leaving it, or the `trigger.dateField` value. */
        anchorAt: instantSchema.nullable(),
        /** When the run was scheduled: `anchorAt` + `trigger.delayMinutes`, moved into the trigger's days and hours. */
        dueAt: instantSchema.nullable(),
    }),
    run: z.object({ attempt: z.number().int().min(1) }),
    subject: z.object({ type: triggerSubjectSchema, id: idSchema }),
    /** Where the subject sits in the trigger's pipeline now; null once it left it. */
    placement: payloadPlacementSchema.nullable(),
    /** The subject's project; null only for a `candidate` (Candidate Flow) run. */
    project: looseRecord({
        /** What a seed attached from an outside list (the upsell sheet); null for most projects. */
        metadata: z.record(z.string(), z.unknown()).nullable(),
        owner: payloadPersonSchema,
        custom,
        pipelines: z.array(payloadPlacementSchema),
        company: looseRecord({ owner: payloadPersonSchema, custom }).nullable(),
        /**
         * The project's contacts (primary first), then any job's hiring manager who is not one of
         * them, with `onProject` false and no `role`.
         */
        contacts: z.array(looseRecord({
            owner: payloadPersonSchema,
            custom,
            role: z.string().nullable(),
            isPrimary: z.boolean(),
            onProject: z.boolean(),
        })),
        deals: z.array(looseRecord({ status: payloadStageSchema, custom })),
        jobs: z.array(looseRecord({
            status: payloadStageSchema,
            recruiter: payloadPersonSchema,
            coordinator: payloadPersonSchema,
            accountManager: payloadPersonSchema,
            /** The hiring manager's entry in `project.contacts`. */
            hiringManagerContactId: idSchema.nullable(),
            custom,
            shortlists: z.array(looseRecord({
                createdBy: payloadPersonSchema,
                candidates: z.array(looseRecord({
                    stage: payloadStageSchema,
                    addedBy: payloadPersonSchema,
                    candidate: looseRecord({ owner: payloadPersonSchema }),
                })),
            })),
        })),
    }).nullable(),
    /** The candidate a `candidate` (Candidate Flow) trigger fired for; null for every other subject. */
    candidate: looseRecord({ owner: payloadPersonSchema, custom }).nullable(),
    /**
     * The shortlisted candidate a `shortlist` trigger fired for; null for any other subject. Its job and
     * shortlist are the ones `jobId` and `shortlistId` name in `project.jobs`.
     */
    shortlistCandidate: looseRecord({
        jobId: idSchema,
        shortlistId: idSchema,
        stage: payloadStageSchema,
        addedBy: payloadPersonSchema,
        candidate: looseRecord({ owner: payloadPersonSchema, custom }),
    }).nullable(),
});
const EX = {
    org: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1001',
    trigger: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1002',
    run: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1003',
    project: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1004',
    pipeline: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1005',
    stage: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1006',
    company: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1007',
    contact: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1008',
    owner: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1009',
    event: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f100a',
    place: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f100b',
    job: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f100c',
    shortlist: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f100d',
    candidate: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f100e',
    otherStage: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f100f',
    jobStage: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1010',
    field: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1011',
    jobField: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1012',
    deal: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1013',
    dealStage: '0b6f7c2e-1d1a-4c51-9a0e-6a3c5d2f1014',
};
const EX_MOVED_AT = '2026-10-30T23:09:46.000Z';
const EX_DATE = '2026-11-05T15:00:00.000Z';
/**
 * What a run of this automation sends, around an example project: the trigger form's preview.
 * The envelope is the automation's own; the records are made up.
 */
export function triggerPayloadExample(t) {
    const stage = {
        id: t.stage.id,
        name: t.stage.name,
        slug: t.stage.slug,
        terminalKind: t.stage.terminalKind ?? null,
    };
    // The stage on the other side of the move: where it came from, or where it went.
    const other = (name) => ({
        id: EX.otherStage,
        name,
        slug: name.toLowerCase().replace(/ /g, '-'),
        terminalKind: null,
    });
    const move = t.kind === 'stage_entered'
        ? { fromStage: other('Previous stage'), toStage: stage }
        : t.kind === 'stage_left'
            ? { fromStage: stage, toStage: other('Next stage') }
            : { fromStage: null, toStage: null };
    const sales = {
        pipeline: { id: EX.pipeline, name: 'Sales Flow' },
        stage: { id: EX.stage, name: 'Active', slug: 'active', terminalKind: 'won' },
        stageEnteredAt: '2026-10-01T12:00:00.000Z',
        archivedAt: null,
    };
    const here = {
        pipeline: { id: t.pipeline.id, name: t.pipeline.name },
        stage: move.toStage ?? stage,
        stageEnteredAt: EX_MOVED_AT,
        archivedAt: null,
    };
    const anchorAt = t.kind === 'relative_to_date' ? EX_DATE : EX_MOVED_AT;
    const dueAt = (t.schedule
        ? triggerDueAt({ ...t.schedule, delayMinutes: t.delayMinutes }, Date.parse(anchorAt))
        : new Date(Date.parse(anchorAt) + t.delayMinutes * 60_000)).toISOString();
    const record = t.dateField?.startsWith('job.') ? 'job' : 'candidate';
    const MOVE_EVENTS = {
        project: 'crm.project.stage_changed',
        deal: 'crm.deal.status_changed',
        job: 'crm.job.status_changed',
        candidate: 'crm.candidate.pipeline_stage_changed',
        shortlist: 'crm.candidate.stage_changed',
    };
    const eventName = t.kind === 'relative_to_date' ? `crm.${record}.updated` : MOVE_EVENTS[t.subject];
    const isShortlist = t.subject === 'shortlist';
    const subjectId = {
        project: EX.project,
        deal: EX.deal,
        job: EX.job,
        candidate: EX.candidate,
        shortlist: EX.place,
    }[t.subject];
    const ana = {
        id: EX.owner,
        kind: 'member',
        name: 'Ana Account',
        email: 'ana@example.com',
    };
    const jane = {
        id: EX.contact,
        firstName: 'Jane',
        lastName: 'Doe',
        email: 'jane@acme.com',
        phone: '+1 555 0100',
        owner: ana,
        custom: [
            {
                id: EX.field,
                key: 'contact_source',
                label: 'Contact Source',
                type: 'text',
                value: null,
                displayValue: null,
            },
        ],
    };
    const candidate = {
        id: EX.candidate,
        firstName: 'Sam',
        lastName: 'Lee',
        email: 'sam@example.com',
    };
    return {
        id: EX.run,
        occurredAt: dueAt,
        organisationId: EX.org,
        trigger: {
            id: EX.trigger,
            name: t.name,
            kind: t.kind,
            delayMinutes: t.delayMinutes,
            dateField: t.dateField,
            taskType: t.taskType,
            taskTypeLabel: TASK_TYPE_LABELS[t.taskType],
        },
        cause: {
            event: {
                id: EX.event,
                name: eventName,
                occurredAt: EX_MOVED_AT,
                actor: { kind: 'user', user: ana },
            },
            ...move,
            anchorAt,
            dueAt,
        },
        run: { attempt: 1 },
        subject: { type: t.subject, id: subjectId },
        placement: here,
        project: t.subject === 'candidate'
            ? null
            : {
                id: EX.project,
                name: 'Acme virtual assistants',
                description: null,
                owner: ana,
                externalIds: {},
                metadata: { list: 'automated', role_to_offer: 'Customer Support', vas_left: 1 },
                createdAt: '2026-10-01T12:00:00.000Z',
                updatedAt: EX_MOVED_AT,
                custom: [],
                pipelines: [t.subject === 'project' ? here : sales],
                company: {
                    id: EX.company,
                    name: 'Acme Inc.',
                    domain: 'acme.com',
                    owner: ana,
                    custom: [],
                },
                contacts: [{ ...jane, role: 'Hiring manager', isPrimary: true, onProject: true }],
                deals: [
                    {
                        id: EX.deal,
                        title: 'Placement fee',
                        value: '1500.00',
                        currency: 'USD',
                        billingModel: 'placement_fee',
                        status: {
                            id: EX.dealStage,
                            name: 'Invoiced',
                            slug: 'invoiced',
                            terminalKind: null,
                        },
                        custom: [],
                    },
                ],
                jobs: [
                    {
                        id: EX.job,
                        title: 'Executive assistant',
                        status: { id: EX.jobStage, name: 'Open', slug: 'open', terminalKind: null },
                        interviewDateTime: EX_DATE,
                        recruiter: ana,
                        coordinator: null,
                        accountManager: ana,
                        dealId: EX.deal,
                        hiringManagerContactId: EX.contact,
                        custom: [
                            {
                                id: EX.jobField,
                                key: 'hiring_regions',
                                label: 'Hiring regions',
                                type: 'single_select',
                                value: 'latam',
                                displayValue: 'LATAM',
                            },
                        ],
                        shortlists: [
                            {
                                id: EX.shortlist,
                                name: 'Shortlist 1',
                                status: 'presented',
                                createdBy: ana,
                                candidates: !isShortlist
                                    ? []
                                    : [
                                        {
                                            id: EX.place,
                                            stage: here.stage,
                                            addedBy: ana,
                                            candidate: { ...candidate, owner: ana },
                                        },
                                    ],
                            },
                        ],
                    },
                ],
            },
        candidate: t.subject === 'candidate'
            ? { ...candidate, interviewDate: EX_DATE, owner: ana, custom: [] }
            : null,
        shortlistCandidate: !isShortlist
            ? null
            : {
                id: EX.place,
                jobId: EX.job,
                shortlistId: EX.shortlist,
                stage: here.stage,
                stageEnteredAt: EX_MOVED_AT,
                visibleToClient: true,
                addedBy: ana,
                candidate: {
                    ...candidate,
                    interviewDate: EX_DATE,
                    owner: ana,
                    custom: [],
                },
            },
    };
}
//# sourceMappingURL=triggers.js.map